import React, { useMemo, Component } from 'react';
import { ComponentType } from 'react';
import { useData } from '../../store/DataContext';
import { AlertTriangleIcon, ArrowUpRightIcon, BanknoteIcon, CheckCircle2Icon, Clock3Icon, CreditCardIcon, FileTextIcon, LandmarkIcon, PlusIcon, ReceiptTextIcon, UsersIcon } from 'lucide-react';
import { BillingSection } from './BillingModule';
import { formatCurrency } from './utils';
type Props = {
  onNavigate: (section: BillingSection) => void;
};
type PriorityCardProps = {
  label: string;
  value: string;
  caption: string;
  icon: ComponentType<{
    className?: string;
  }>;
  tone: 'blue' | 'emerald' | 'amber' | 'slate';
};
export function BillingDashboard({
  onNavigate
}: Props) {
  const {
    currentUser,
    invoices,
    walkInClients,
    patients,
    quotations,
    payments
  } = useData();
  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const currentMonth = today.slice(0, 7);
    const todayPayments = payments.filter(payment => payment.date.startsWith(today));
    const monthPayments = payments.filter(payment => payment.date.startsWith(currentMonth));
    const outstandingInvoices = invoices.filter(invoice => invoice.status === 'Unpaid' || invoice.status === 'Partially Paid' || invoice.status === 'Overdue');
    const overdueInvoices = invoices.filter(invoice => invoice.status === 'Overdue' || (invoice.status === 'Unpaid' || invoice.status === 'Partially Paid') && invoice.dueDate < today);
    const draftInvoices = invoices.filter(invoice => invoice.status === 'Draft');
    const recentInvoices = [...invoices].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 6);
    return {
      todayRevenue: todayPayments.reduce((sum, payment) => sum + payment.amount, 0),
      monthRevenue: monthPayments.reduce((sum, payment) => sum + payment.amount, 0),
      outstandingAmount: outstandingInvoices.reduce((sum, invoice) => sum + invoice.balanceDue, 0),
      outstandingCount: outstandingInvoices.length,
      overdueCount: overdueInvoices.length,
      overdueAmount: overdueInvoices.reduce((sum, invoice) => sum + invoice.balanceDue, 0),
      paidCount: invoices.filter(invoice => invoice.status === 'Paid').length,
      draftCount: draftInvoices.length,
      quotationCount: quotations.length,
      receiptsIssued: payments.length,
      totalCustomers: patients.length + walkInClients.length,
      registeredCustomers: patients.length,
      walkInCustomers: walkInClients.length,
      recentInvoices
    };
  }, [invoices, payments, patients, quotations, walkInClients]);
  const firstName = currentUser?.fullName.split(' ')[0] || 'there';
  const currentHour = new Date().getHours();
  const greeting = currentHour < 12 ? 'Good morning' : currentHour < 18 ? 'Good afternoon' : 'Good evening';
  const getCustomerName = (customerId: string, type: 'Patient' | 'WalkIn', savedName?: string) => {
    if (type === 'Patient') {
      const patient = patients.find(item => item.id === customerId);
      return patient ? `${patient.firstName} ${patient.lastName}` : 'Registered patient';
    }
    return savedName || walkInClients.find(item => item.id === customerId)?.name || 'Walk-in client';
  };
  return <div className="space-y-7" data-id="element-886">
      <section className="flex flex-col justify-between gap-4 border-b border-slate-200 pb-6 lg:flex-row lg:items-end" data-id="element-887">
        <div data-id="element-888">
          <p className="text-sm font-semibold text-pink-600" data-id="element-889">
            BMED Care billing desk
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl" data-id="element-890">
            {greeting}, {firstName}.
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600" data-id="element-891">
            Stay ahead of today’s collections, outstanding patient balances, and
            visit-based charges.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-892">
          <button type="button" onClick={() => onNavigate('customers')} className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50" data-id="element-893">
            <UsersIcon className="mr-1.5 h-4 w-4" data-id="element-894" /> Find customer
          </button>
          <button type="button" onClick={() => onNavigate('new-invoice')} className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700" data-id="element-895">
            <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-896" /> Create invoice
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-id="element-897">
        <PriorityCard label="Collected today" value={formatCurrency(stats.todayRevenue)} caption={`${payments.length ? `${stats.receiptsIssued} receipts issued overall` : 'No payments recorded today'}`} icon={BanknoteIcon} tone="blue" data-id="element-898" />
        <PriorityCard label="Month to date" value={formatCurrency(stats.monthRevenue)} caption={`${stats.paidCount} invoices fully settled`} icon={LandmarkIcon} tone="emerald" data-id="element-899" />
        <PriorityCard label="Outstanding balances" value={formatCurrency(stats.outstandingAmount)} caption={`${stats.outstandingCount} invoices need follow-up`} icon={Clock3Icon} tone="amber" data-id="element-900" />
        <PriorityCard label="Overdue now" value={String(stats.overdueCount)} caption={stats.overdueCount ? `${formatCurrency(stats.overdueAmount)} past due` : 'No overdue accounts'} icon={AlertTriangleIcon} tone="slate" data-id="element-901" />
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.8fr)]" data-id="element-902">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-903">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4" data-id="element-904">
            <div data-id="element-905">
              <h3 className="font-bold text-slate-900" data-id="element-906">
                Collection priorities
              </h3>
              <p className="mt-0.5 text-sm text-slate-500" data-id="element-907">
                What needs attention across the billing desk.
              </p>
            </div>
            <button type="button" onClick={() => onNavigate('invoices-unpaid')} className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700" data-id="element-908">
              Review receivables <ArrowUpRightIcon className="ml-1 h-4 w-4" data-id="element-909" />
            </button>
          </div>
          <div className="grid grid-cols-1 divide-y divide-slate-100 md:grid-cols-3 md:divide-x md:divide-y-0" data-id="element-910">
            <PriorityLink title="Unpaid invoices" detail={`${stats.outstandingCount} records with balances`} value={formatCurrency(stats.outstandingAmount)} icon={CreditCardIcon} tone="amber" onClick={() => onNavigate('invoices-unpaid')} data-id="element-911" />
            <PriorityLink title="Drafts to finish" detail="Review and send prepared bills" value={String(stats.draftCount)} icon={FileTextIcon} tone="blue" onClick={() => onNavigate('invoices-draft')} data-id="element-912" />
            <PriorityLink title="Open quotations" detail="Convert accepted estimates faster" value={String(stats.quotationCount)} icon={ReceiptTextIcon} tone="emerald" onClick={() => onNavigate('quotations')} data-id="element-913" />
          </div>
        </div>

        <aside className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-914">
          <div className="flex items-center justify-between" data-id="element-915">
            <div data-id="element-916">
              <h3 className="font-bold text-slate-900" data-id="element-917">Customer mix</h3>
              <p className="mt-0.5 text-sm text-slate-500" data-id="element-918">
                Billing profiles available today.
              </p>
            </div>
            <UsersIcon className="h-5 w-5 text-slate-400" data-id="element-919" />
          </div>
          <div className="mt-6 space-y-4" data-id="element-920">
            <CustomerMixRow label="Registered patients" count={stats.registeredCustomers} total={stats.totalCustomers} color="bg-blue-600" data-id="element-921" />
            <CustomerMixRow label="Walk-in clients" count={stats.walkInCustomers} total={stats.totalCustomers} color="bg-pink-500" data-id="element-922" />
          </div>
          <button type="button" onClick={() => onNavigate('customers')} className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-id="element-923">
            Manage customers
          </button>
        </aside>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-924">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-925">
          <div data-id="element-926">
            <h3 className="font-bold text-slate-900" data-id="element-927">Recent invoices</h3>
            <p className="mt-0.5 text-sm text-slate-500" data-id="element-928">
              Latest billing activity from all hospital departments.
            </p>
          </div>
          <button type="button" onClick={() => onNavigate('invoices-sent')} className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700" data-id="element-929">
            View issued invoices <ArrowUpRightIcon className="ml-1 h-4 w-4" data-id="element-930" />
          </button>
        </div>
        {stats.recentInvoices.length > 0 ? <div className="overflow-x-auto" data-id="element-931">
            <table className="min-w-full divide-y divide-slate-100" data-id="element-932">
              <thead className="bg-slate-50/70" data-id="element-933">
                <tr data-id="element-934">
                  {['Invoice', 'Customer', 'Created', 'Status', 'Balance'].map(heading => <th key={heading} className={`px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 ${heading === 'Balance' ? 'text-right' : 'text-left'}`} data-id="element-935">
                        {heading}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white" data-id="element-936">
                {stats.recentInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-slate-50/70" data-id="element-937">
                    <td className="whitespace-nowrap px-5 py-3.5 text-sm font-semibold text-blue-700" data-id="element-938">
                      {invoice.invoiceNumber}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-700" data-id="element-939">
                      {getCustomerName(invoice.customerId, invoice.customerType, invoice.customerName)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-500" data-id="element-940">
                      {new Date(invoice.createdAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5" data-id="element-941">
                      <InvoiceStatus status={invoice.status} data-id="element-942" />
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-semibold text-slate-900" data-id="element-943">
                      {formatCurrency(invoice.balanceDue)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div> : <div className="flex flex-col items-center px-5 py-14 text-center" data-id="element-944">
            <div className="rounded-full bg-blue-50 p-3 text-blue-600" data-id="element-945">
              <FileTextIcon className="h-6 w-6" data-id="element-946" />
            </div>
            <h4 className="mt-4 font-semibold text-slate-900" data-id="element-947">
              Start today’s billing desk
            </h4>
            <p className="mt-1 max-w-sm text-sm leading-6 text-slate-500" data-id="element-948">
              Create an invoice for a registered patient, pull charges from
              their visit, or bill a walk-in client.
            </p>
            <button type="button" onClick={() => onNavigate('new-invoice')} className="mt-5 inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700" data-id="element-949">
              <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-950" /> Create first invoice
            </button>
          </div>}
      </section>
    </div>;
}
function PriorityCard({
  label,
  value,
  caption,
  icon: Icon,
  tone
}: PriorityCardProps) {
  const tones = {
    blue: 'bg-blue-50 text-blue-700 ring-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100',
    slate: 'bg-slate-100 text-slate-700 ring-slate-200'
  };
  return <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-951">
      <div className="flex items-start justify-between gap-3" data-id="element-952">
        <div data-id="element-953">
          <p className="text-sm font-medium text-slate-500" data-id="element-954">{label}</p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-slate-950" data-id="element-955">
            {value}
          </p>
        </div>
        <div className={`rounded-lg p-2.5 ring-1 ${tones[tone]}`} data-id="element-956">
          <Icon className="h-5 w-5" data-id="element-957" />
        </div>
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-500" data-id="element-958">{caption}</p>
    </div>;
}
function PriorityLink({
  title,
  detail,
  value,
  icon: Icon,
  tone,
  onClick
}: {
  title: string;
  detail: string;
  value: string;
  icon: ComponentType<{
    className?: string;
  }>;
  tone: 'blue' | 'emerald' | 'amber';
  onClick: () => void;
}) {
  const iconTone = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600'
  };
  return <button type="button" onClick={onClick} className="group w-full p-5 text-left transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500" data-id="element-959">
      <div className="flex items-start justify-between gap-3" data-id="element-960">
        <div className={`rounded-md p-2 ${iconTone[tone]}`} data-id="element-961">
          <Icon className="h-4 w-4" data-id="element-962" />
        </div>
        <ArrowUpRightIcon className="h-4 w-4 text-slate-300 transition-colors group-hover:text-blue-600" data-id="element-963" />
      </div>
      <p className="mt-4 text-sm font-semibold text-slate-900" data-id="element-964">{title}</p>
      <p className="mt-1 text-2xl font-bold text-slate-950" data-id="element-965">{value}</p>
      <p className="mt-1 text-xs leading-5 text-slate-500" data-id="element-966">{detail}</p>
    </button>;
}
function CustomerMixRow({
  label,
  count,
  total,
  color
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const percentage = total > 0 ? Math.round(count / total * 100) : 0;
  return <div data-id="element-967">
      <div className="mb-2 flex items-center justify-between text-sm" data-id="element-968">
        <span className="font-medium text-slate-700" data-id="element-969">{label}</span>
        <span className="font-semibold text-slate-900" data-id="element-970">{count}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100" data-id="element-971">
        <div className={`h-full rounded-full ${color}`} style={{
        width: `${percentage}%`
      }} data-id="element-972" />
      </div>
      <p className="mt-1 text-xs text-slate-500" data-id="element-973">
        {percentage}% of billing profiles
      </p>
    </div>;
}
function InvoiceStatus({
  status
}: {
  status: string;
}) {
  const className = {
    Paid: 'bg-emerald-50 text-emerald-700',
    'Partially Paid': 'bg-blue-50 text-blue-700',
    Unpaid: 'bg-amber-50 text-amber-700',
    Overdue: 'bg-red-50 text-red-700',
    Draft: 'bg-slate-100 text-slate-600',
    Sent: 'bg-violet-50 text-violet-700'
  }[status] || 'bg-slate-100 text-slate-600';
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${className}`} data-id="element-974">
      {status}
    </span>;
}